/**
 * A note as slides. Slides are separated by a line with `---` (with an empty line before it, as in
 * Marp and most Markdown slide tools). A note without such lines is cut at its titles (# and ##), so
 * any note can be presented. In a slide, a line starting with "Notes:" begins the speaker notes.
 */
export interface Slide {
  /** The slide's Markdown (without the speaker notes). */
  md: string;
  /** Speaker notes, shown only in the presenter view. */
  notes: string;
  /** The slide's first title, for the overview and the counter. */
  title: string;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const RULE = /^ {0,3}---+\s*$/;
const HEADING = /^ {0,3}(#{1,2})\s+(.*?)\s*#*\s*$/;
const ANY_HEADING = /^ {0,3}#{1,6}\s+(.*?)\s*#*\s*$/;
const NOTES = /^\s*notes?\s*:\s?/i;
const FRONT_MATTER_LINE = /^[A-Za-z][\w-]*\s*:/;

/** Lines outside fenced code blocks (true) or inside them (false). */
function outsideCode(lines: string[]) {
  const out: boolean[] = [];
  let fence: string | null = null;
  for (const l of lines) {
    const f = FENCE.exec(l);
    if (fence) {
      out.push(false);
      if (f && f[1][0] === fence[0] && f[1].length >= fence.length) fence = null;
    } else if (f) {
      fence = f[1];
      out.push(false);
    } else out.push(true);
  }
  return out;
}

/** Leading Marp/YAML settings (--- key: value --- at the very top) are not a slide. */
function withoutFrontMatter(lines: string[]) {
  if (!RULE.test(lines[0] ?? "")) return lines;
  const end = lines.findIndex((l, i) => i > 0 && RULE.test(l));
  if (end > 0 && end <= 30 && lines.slice(1, end).every((l) => !l.trim() || FRONT_MATTER_LINE.test(l))) return lines.slice(end + 1);
  return lines;
}

function slideOf(lines: string[]): Slide | null {
  const notesAt = lines.findIndex((l, i) => NOTES.test(l) && outsideCode(lines)[i]);
  const body = (notesAt >= 0 ? lines.slice(0, notesAt) : lines).join("\n").trim();
  const notes = notesAt >= 0 ? [lines[notesAt].replace(NOTES, ""), ...lines.slice(notesAt + 1)].join("\n").trim() : "";
  if (!body && !notes) return null;
  const title = body.split("\n").map((l) => ANY_HEADING.exec(l)?.[1]).find(Boolean) ?? body.split("\n").find((l) => l.trim())?.trim() ?? "";
  return { md: body, notes, title: title.replace(/[*_`[\]]/g, "").slice(0, 120) };
}

export function splitSlides(markdown: string): Slide[] {
  const lines = withoutFrontMatter(markdown.replace(/\r\n?/g, "\n").split("\n"));
  const code = outsideCode(lines);
  // A rule with an empty line (or the start) before it; "Text\n---" is a heading in Markdown.
  const rules = lines.map((l, i) => code[i] && RULE.test(l) && (i === 0 || !lines[i - 1].trim()));
  const parts: string[][] = [[]];
  if (rules.some(Boolean)) {
    lines.forEach((l, i) => (rules[i] ? parts.push([]) : parts.at(-1)!.push(l)));
  } else {
    // No separators: each # or ## title starts a slide.
    lines.forEach((l, i) => {
      if (code[i] && HEADING.test(l) && parts.at(-1)!.some((x) => x.trim())) parts.push([]);
      parts.at(-1)!.push(l);
    });
  }
  return parts.map(slideOf).filter((s): s is Slide => !!s);
}

/** Whether the note has its own slide separators (rather than being cut at its titles). */
export function hasSlideBreaks(markdown: string) {
  const lines = withoutFrontMatter(markdown.replace(/\r\n?/g, "\n").split("\n"));
  const code = outsideCode(lines);
  return lines.some((l, i) => code[i] && RULE.test(l) && (i === 0 || !lines[i - 1].trim()));
}
